/**
 * CouponSelectionScreen - Container Component
 *
 * Full screen/modal for selecting a coupon to apply to cart.
 * Shows manual input, available coupons/vouchers, and ineligible items.
 *
 * Uses useSalesRuleList() which merges coupons + vouchers into unified sections
 * with condition evaluation results (ineligibilityReasons, applicableAmount).
 *
 * @see mobile: module-flash-sale/sales-rule/screens/coupon-screeen/
 */

'use client';

import React, { useMemo } from 'react';
import { CouponSelectionScreenView } from './coupon-selection-screen-view';
import type { CouponSelectionScreenProps } from './coupon-selection-screen.types';
import {
    useCouponHandler,
    useSalesRuleList,
    withCouponProvider,
    type SalesRule,
    type AppliedUniversalCode,
} from '@kidslabs/module-flash-sale';
import { withDialog, type WithDialogProps } from '@kidslabs/mario-nextjs-react-ui-core';

const CouponSelectionScreenInner: React.FC<CouponSelectionScreenProps & WithDialogProps> = ({
    onClose,
    variant = 'mini',
    setDialogOptions,
    showDialog,
    hideDialog,
}) => {
    const {
        selectedCoupon,
        onSelectCoupon,
        appliedCoupons,
        appliedVouchers,
        loadingAvailableCoupons,
        loadingAppliedCoupons,
        validatingCoupons,
        salesRuleSections,
        onRefresh,
    } = useSalesRuleList();

    const filteredAppliedCoupons = useMemo(
        () => (appliedCoupons ?? []).filter((item): item is AppliedUniversalCode => !!item),
        [appliedCoupons]
    );

    const {
        couponText,
        errorMessage,
        isButtonDisabled,
        removeLoading,
        applyLoading,
        handleOnChangeText,
        handleApplyCode,
        isSelectedCoupon,
        handleSelectedCouponPress,
    } = useCouponHandler({
        onSuccess: onClose,
        appliedCoupons: filteredAppliedCoupons,
        appliedVouchers,
        setDialogOptions,
        showDialog,
        hideDialog,
        onRefresh,
    });

    const isLoading = loadingAvailableCoupons || loadingAppliedCoupons || validatingCoupons;
    const isProcessing = removeLoading || applyLoading;

    return (
        <CouponSelectionScreenView
            variant={variant}
            couponSections={salesRuleSections}
            selectedCoupon={selectedCoupon as SalesRule | undefined}
            appliedCouponCode={filteredAppliedCoupons[0]?.code}
            isLoading={isLoading}
            isProcessing={isProcessing}
            couponText={couponText}
            errorMessage={errorMessage}
            isButtonDisabled={isButtonDisabled}
            isSelectedCoupon={isSelectedCoupon}
            onSelectCoupon={onSelectCoupon}
            onChangeText={handleOnChangeText}
            onApplyCoupon={handleApplyCode}
            onSubmit={handleSelectedCouponPress}
            onRefresh={onRefresh}
            onClose={onClose}
        />
    );
};

const CouponSelectionScreenWithDialog = withDialog(CouponSelectionScreenInner);
const CouponSelectionScreenWithProvider = withCouponProvider(CouponSelectionScreenWithDialog);

export const CouponSelectionScreen: React.FC<CouponSelectionScreenProps> = props => {
    return <CouponSelectionScreenWithProvider {...props} />;
};

CouponSelectionScreen.displayName = 'CouponSelectionScreen';
